import { can } from "@bt/domain";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ShieldIcon, WarehouseIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Page, PageHeader } from "@/components/app/page";
import { Members } from "@/features/members/members";
import { About } from "@/features/settings/about";
import { ChassisCodes } from "@/features/settings/chassis-codes";
import { Data } from "@/features/settings/data";
import { GarageSettings } from "@/features/settings/garage-settings";
import { General } from "@/features/settings/general";
import { Installation, useWholeApp } from "@/features/settings/installation";
import { Locations } from "@/features/settings/locations";
import { Section } from "@/features/settings/section";
import { Suppliers } from "@/features/settings/suppliers";
import { Tags } from "@/features/settings/tags";
import { hasAccounts } from "@/lib/account";
import { garage } from "@/lib/garage";
import { useSystemInfo } from "@/lib/system";
import { useTRPC } from "@/lib/trpc";

export const Route = createFileRoute("/settings")({
  component: SettingsPage,
});

const SECTIONS = [
  "general",
  "members",
  "locations",
  "suppliers",
  "tags",
  "chassisCodes",
  "installation",
  "about",
] as const;

/** Garage settings: only for garage admins (architecture.md › Garage settings). */
const GARAGE_SECTIONS = ["garage", "data"] as const;

function SettingsPage() {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const info = useSystemInfo();
  const wholeApp = useWholeApp();
  // Backups, export and restore: for whoever may use the whole installation, where the host has them.
  const { data: installation } = useQuery({
    ...trpc.installation.info.queryOptions(),
    enabled: wholeApp,
  });
  const { data: access } = useQuery(trpc.garages.access.queryOptions());
  const manage = access ? can(access, "garage.manage") : false;
  const caps = info?.capabilities;
  // The garage's demo packages, for garage admins where the host allows them.
  const showData =
    manage &&
    Boolean(caps?.demoImport || caps?.demoExport || caps?.demoGenerate || caps?.demoDownloadUrl);
  const sections = SECTIONS.filter(
    (section) =>
      (section !== "members" || hasAccounts()) &&
      (section !== "installation" || Boolean(installation)),
  );
  const garageSections = manage
    ? GARAGE_SECTIONS.filter((section) => section !== "data" || showData)
    : [];
  return (
    <Page>
      <PageHeader title={t("settings.title")} />
      <div className="grid gap-8 lg:grid-cols-[200px_1fr]">
        <nav className="sticky top-6 hidden h-fit flex-col gap-0.5 lg:flex">
          {sections.map((section) => (
            <NavLink key={section} section={section} />
          ))}
          {garageSections.length ? (
            <>
              <div className="mt-4 flex items-center gap-1.5 px-3 pb-1 text-xs font-medium text-muted-foreground">
                <WarehouseIcon className="size-3.5" />
                {t("settings.garageSettings")}
              </div>
              {garageSections.map((section) => (
                <NavLink key={section} section={section} />
              ))}
            </>
          ) : null}
        </nav>
        <div className="flex min-w-0 flex-col gap-10">
          <General />
          {hasAccounts() ? (
            <Section id="members" title={t("members.title")} description={t("members.body")}>
              <Members garageName={garage ?? ""} />
            </Section>
          ) : null}
          <Locations />
          <Suppliers />
          <Tags />
          <ChassisCodes />
          {installation ? <Installation /> : null}
          <About />
          {garageSections.length ? (
            <div className="flex flex-col gap-10 border-t border-border pt-8">
              <div className="-mb-4">
                <h2 className="flex items-center gap-2 text-xl font-semibold tracking-tight">
                  <WarehouseIcon className="size-5 text-muted-foreground" />
                  {t("settings.garageSettings")}
                </h2>
                {hasAccounts() ? (
                  <p className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
                    <ShieldIcon className="size-3.5 shrink-0" />
                    {t("settings.garageSettingsBody")}
                  </p>
                ) : null}
              </div>
              <GarageSettings />
              {showData ? <Data /> : null}
            </div>
          ) : null}
        </div>
      </div>
    </Page>
  );
}

function NavLink({
  section,
}: {
  section: (typeof SECTIONS)[number] | (typeof GARAGE_SECTIONS)[number];
}) {
  const { t } = useTranslation();
  return (
    <a
      href={`#${section}`}
      className="rounded-lg px-3 py-1.5 text-sm text-muted-foreground transition hover:bg-muted hover:text-foreground"
    >
      {t(`settings.${section}`)}
    </a>
  );
}
